import { StateService } from '@uirouter/angular';
import { Injectable } from '@angular/core';
import * as _ from 'lodash-es';

import { UserService } from './core.user.service';
import { GfTranslateService } from '../services/core.translate.service';

@Injectable()
export class FilterService {
    
    constructor(
        private userService: UserService,
        private stateService: StateService,
        private gfTranslateService: GfTranslateService)
    {}

    public valueOrDefault(val, def) {
        return (val != undefined) ? val : def;
    }

    public getParams(p) {
        const def = _.pickBy(
            this.stateService.params,
            function(value, key) {
                return (! key.startsWith('$'));
            }
        );

        const params = this.valueOrDefault((_.isEmpty(p) ? undefined : p), def);

        if (!_.isPlainObject(params)) {
            return this.decodeParamValue(params);
        }

        return _.mapValues(params, (value) => this.decodeParamValue(value));
    }

    private decodeParamValue(value: any): any {
        // Only normalize values that show signs of double-encoding (e.g. %2520).
        // This avoids altering ordinary query strings while fixing repeated encoding.
        if (typeof value !== 'string' || value.indexOf('%25') === -1) {
            return value;
        }

        let decoded = value;
        // Safety cap: handles repeated encoding while avoiding unbounded decode loops.
        for (let i = 0; i < 5; i++) {
            try {
                const next = decodeURIComponent(decoded);
                if (next === decoded) {
                    break;
                }
                decoded = next;
            } catch (err) {
                break;
            }
        }

        return decoded;
    }

    public currentUser() { 
        return this.userService.session.getUser(); 
    }

    public getSortValues(defaultField: string = '', defaultDir: string = '', params: any = {}) {
        var p = this.getParams(params);
        var f = this.valueOrDefault(defaultField, 'id');
        var d = this.valueOrDefault(defaultDir, 'asc');
        return {
            sort_field: _.get(p, 'sort_field', f),
            sort_dir: _.get(p, 'sort_dir', d)
        };
    }

    public getPagingValues(defaultPage: any = '', defaultSize: number = 20, params: any = {}) {
        var p = this.getParams(params);
        var n = this.valueOrDefault(defaultPage, 1);
        var s = this.valueOrDefault(defaultSize, 20);
        return {
            page_num: _.get(p, 'page_num', n),
            page_size: _.get(p, 'page_size', s)
        };
    }

    public getAnyFilterObj(isDefault = false) {
        var d = this.valueOrDefault(isDefault, true);
        return { id: '', label: this.gfTranslateService.translate("Any"), defaultSelected: d };
    }

    public anyFiltersSet(baseFilters, params = {}) {
        var p = this.getParams(params);
        // List of field names to check in params list
        var list = _.map(baseFilters, 'alias');

        // Get only the keys with a value set
        var ret = Object.keys(_.pickBy(p, function (item, key) {
            return list.includes(key) && _.get(p, key, '') != '';
        }));

        return ret.length > 0;
    }

    public getAllFilterValues(filters) {
        return _.reduce(
            filters,
            function (carry, f) {
                var key = f.alias;
                var val = _.get(f, 'value.id', _.get(f, 'value', ''));

                carry[key] = val;

                return carry;
            },
            {}
        );
    }

    public getFilterValues(filters, evenIfEmpty: boolean = false) {
        return _.reduce(
            filters,
            function (carry, f) {
                var key = f.alias;
                var val = _.get(f, 'value.id', _.get(f, 'value', ''));

                if (val != '' || evenIfEmpty) {
                    carry[key] = val;
                }

                return carry;
            },
            {}
        );
    }

    public setFilterValues(filters, params = {}) {
        var p = this.getParams(params);
        return _.map(
            filters,
            function (f) {
                var value = _.get(p, f.alias, '');
                var valObject = _.find(
                    _.get(f, 'fieldElements', []),
                    function (item) { return item.id == value; }
                );
                _.set(f, 'value', valObject ? valObject : value);
                return f;
            }
        );
    }

    public getFetchArgs(ex = {}, params: any = {}) {
        ex = this.valueOrDefault(ex, {});
        return _.pickBy(
            _.extend(this.getParams(params), ex),
            function (value) {
                return (undefined != value && null != value && value != '');
            }
        );
    }
}
